import { cn } from '@creator/ui/utils/ui';
import TopicChip from '@src/basic-components/TopicChip/TopicChip';
import { useStoreState } from '@src/model/hooks';
import type { Flair } from '@src/store/flairs/flairs';
import { propsEqualityFn } from '@src/utils/object-utils/object-utils';
import type { FC } from 'react';
import { memo } from 'react';

export interface FlairsCardProps {
    tokenName: string;
    className?: string;
    onFlairClick: (flairId: string) => void;
    selectedFlair?: Flair['id'];
    selectedFlairs?: Flair['id'][];
    isFlairMandatory?: boolean;
    allowDeselect?: boolean;
    multiSelect?: boolean;
}

const FlairsCard: FC<FlairsCardProps> = props => {
    const { allowDeselect, isFlairMandatory = false, selectedFlair, selectedFlairs = [], tokenName, className, multiSelect = false } = props;

    const token = useStoreState(state => state.token.getToken(tokenName));

    const flairs = token ? token.activeFlairs : ([] as Flair[]);

    function onFlairClick(flairId: Flair['id']) {
        const { onFlairClick } = props;

        if (multiSelect) {
            // Multi-select mode: toggle flair selection
            const isSelected = selectedFlairs.includes(flairId);
            if (isFlairMandatory && selectedFlairs.length === 1 && isSelected) {
                // Don't allow deselecting the last flair if mandatory
                return;
            }
            onFlairClick(flairId);
        } else {
            // Single-select mode (legacy behavior)
            if (isFlairMandatory && flairId === selectedFlair) return;
            if (selectedFlair === flairId) return onFlairClick('');
            onFlairClick(flairId);
        }
    }

    function renderFlair(flair: Flair) {
        const { id } = flair;
        const isActive = multiSelect ? selectedFlairs.includes(id) : selectedFlair === id;
        const isPrimary = multiSelect && selectedFlairs.length > 0 && selectedFlairs[0] === id;

        return (
            <TopicChip
                showCloseIcon={allowDeselect || (isActive && !isFlairMandatory)}
                isActive={isActive}
                iconName={isPrimary ? 'starFilled' : undefined}
                iconClassName="text-primary-500 text-xs"
                onClick={() => onFlairClick(id)}
                key={id}
                topicId={id}
            />
        );
    }

    function renderTokenFlairs() {
        if (!flairs || flairs.length === 0) return null;
        const renderedFlairs = flairs.map(renderFlair);
        return renderedFlairs;
    }

    return <div className={cn('flex flex-wrap gap-3', className)}>{renderTokenFlairs()}</div>;
};

export default memo(FlairsCard, propsEqualityFn);
